import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Customer, StaticDocument } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';
import Jsona from 'jsona';

import { select, Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as fromAppRoot from '@app/app.reducers';

import { environment } from '../../../../environments/environment';
import { DocType, GrowDocument, LibertyDocument, NewDocType, PersonalDetails } from '../models/Profile';

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private jsona = new Jsona();
  accountID: string;

  constructor(
    private http: HttpClient,
    private store: Store,
    private translateService: TranslateService
  ) {
    this.store.pipe(select(fromAppRoot.selectCurrentAccount)).subscribe((value) => {
      this.accountID = value.id;
    });
  }

  getPersonalInfo(): Observable<{ data: { attributes: PersonalDetails } }> {
    return this.http.get<{ data: { attributes: PersonalDetails } }>(`${environment.apiUrlV3}/personal_info`);
  }

  updatePersonalInfo(personalDetails: Partial<PersonalDetails>): Observable<{ data: { attributes: Partial<PersonalDetails> } }> {
    return this.http.put<{ data: { attributes: Partial<PersonalDetails> } }>(`${environment.apiUrlV3}/personal_info`, {
      data: { type: 'personal_infos', id: '0', attributes: personalDetails }
    });
  }

  updateOwnerName(accountId: string, owner_name: string): Observable<{ data: { attributes: { owner_name: string } } }> {
    return this.http.put<{ data: { attributes: { owner_name: string } } }>(`${environment.apiUrlV3}/accounts/${accountId}`, {
      data: { type: 'accounts', id: accountId, attributes: { owner_name } }
    });
  }

  validatePassword(body: { password: string }): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${environment.apiUrl}/validate/password`, body);
  }

  getCustomerDocuments(): Observable<{ data: NewDocType[]; meta: [] }> {
    return this.http.get<{ data: NewDocType[]; meta: [] }>(`${environment.apiUrlV3}/customer_documents`);
  }

  getLibertyDocuments(
    perItem: number,
    page: number,
    sort: string,
    filter: string
  ): Observable<{ data: LibertyDocument[]; meta: { stats: { total: { count: number } } } }> {
    let url = `${environment.apiUrlV3}/liberty_documents?stats[total]=count&page[size]=${perItem}&page[number]=${page}&sort=${sort}`;
    if (filter !== 'all') {
      url = `${environment.apiUrlV3}/liberty_documents?stats[total]=count&page[size]=${perItem}&page[number]=${page}&sort=${sort}&filter[document_type_code]=${filter}`;
    }
    return this.http.get<{ data: LibertyDocument[]; meta: { stats: { total: { count: number } } } }>(url);
  }

  getGrowDocuments(): Observable<{ data: GrowDocument[] }> {
    return this.http.get<{ data: GrowDocument[]; meta: [] }>(`${environment.apiUrlV3}/inyova_interest/impact_project_documents`).pipe(
      map((response) => {
        return {
          data: (this.jsona.deserialize({ ...response }) as GrowDocument[]).map((document) => {
            if (document.title) {
              return document;
            }

            let key = '';
            switch (document.type) {
              case 'terms_conditions_sheet':
                key = 'termsConditionsSheet';
                break;
              case 'term_sheet':
                key = 'termSheet';
                break;
              default:
                key = 'factSheet';
            }

            return {
              ...document,
              title: this.translateService.instant(`GROW.project.documents.${key}`)
            };
          })
        };
      })
    );
  }

  send2FAToken(authToken: string): Observable<{
    code: number;
    message: string;
    status: string;
  }> {
    return this.http.post<{
      code: number;
      message: string;
      status: string;
    }>(`${environment.apiUrl}/multi_factor_auth/send_2fa_token?authentication_token=${authToken}`, {});
  }

  toggle2FAAuthorization(otpCode: string): Observable<{
    code: number;
    message: string;
    status: string;
    status_2fa: string;
  }> {
    return this.http.post<{
      code: number;
      message: string;
      status: string;
      status_2fa: string;
    }>(`${environment.apiUrl}/multi_factor_auth/toggle_2fa_token`, {
      otp_code_token: otpCode
    });
  }

  updatePassword(body: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }): Observable<{ data: Customer; status: boolean }> {
    return this.http.put<{ data: Customer; status: boolean }>(`${environment.apiUrl}/auth`, body);
  }

  getChCitiesByZip(zipCode: string): Observable<any> {
    return fromFetch(
      `https://swisspost.opendatasoft.com/api/records/1.0/search/?dataset=plz_verzeichnis_v2&q=&facet=postleitzahl&facet=ortbez18&refine.postleitzahl=${zipCode}`
    ).pipe(
      switchMap((response) => {
        if (response.ok) {
          return response.json();
        } else {
          return throwError({ message: 'Failed to get a city' });
        }
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  getStaticDocuments(filter: null | 'crowd_investing' | '3a' | '3b' = null): Observable<{ data: StaticDocument[]; meta: [] }> {
    return this.http.get<{ data: StaticDocument[]; meta: [] }>(
      `${environment.apiUrlV3}/static_documents${filter === null ? `` : `?filter[account_type]=${filter}`}`
    );
  }
}
